import { Avatar, type AvatarShape, type AvatarSize } from "@cntrl/ui";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { useBandOwnerPersonId } from "../../app/hooks/useBandTeam.js";
import { useBandContextOrNull } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";

/**
 * A PERSON's avatar — the data-aware sibling of the presentational `Avatar`: give it the person +
 * their `avatarMediaId` (the existence/cache-bust signal every person-carrying DTO now exposes) and it
 * resolves the picture through the visibility-gated avatar route (`useAvatarUrl("person", …)` — an
 * authenticated fetch → object URL), falling back to the initials the caller derived. Lives in the APP, not
 * `@cntrl/ui` — it owns a data fetch, and the ui package stays presentational.
 *
 * The route is the GLOBAL one every avatar in the product shares (`media.avatars`, 2026-09-12): a person's
 * bytes were always tenant-less and correct, and the band/org/chat pictures joined them rather than the
 * other way round. `GET /users/{id}/avatar` still answers, delegating to the same service, for clients
 * holding that URL.
 *
 * **The OWNER seat is the band, and is drawn as one.** Per the standing ruling, an artist/owner account has
 * no face of its own — the conversion CLEARED personal owner avatars, and the person-avatar route cannot
 * serve band assets, so `GET /users/{owner}/avatar` correctly resolves nothing and every surface fell back to
 * initials ("MF" where the band's photo belongs). When the person IS this band's owner, the picture therefore
 * comes from the BAND's media instead, and the shape becomes the rounded square that encodes "this is a band,
 * not a person". Both halves of that ruling now hold everywhere this component is used, rather than only on
 * the crew card, which had hand-rolled the band half.
 *
 * Use it anywhere a person's initials render today (crew rows, profile views); orgs and contacts keep plain
 * `Avatar` — their images ride other pipelines.
 */
export function PersonAvatar({
  personId,
  avatarMediaId,
  initials,
  size,
  shape,
  className,
}: {
  personId: string | null | undefined;
  avatarMediaId: string | null | undefined;
  /** Pre-computed, like `Avatar` — person vs band derivation rules differ, so callers own it. */
  initials: string;
  size?: AvatarSize;
  /** Omit it: a person resolves to a circle and the band to a rounded square on their own. Pass one only to
   *  override that, which almost nothing should. */
  shape?: AvatarShape;
  className?: string;
}) {
  const band = useBandContextOrNull();
  const ownerPersonId = useBandOwnerPersonId();
  const me = useMe();
  const isBand = !!personId && !!ownerPersonId && personId === ownerPersonId;

  // The band's MAIN photo, off the me-document's seat for this band — the same pair the shell's band switcher
  // renders. Passing null when this isn't the owner keeps the hook inert rather than conditional.
  //
  // BOTH branches are the same hook now, differing only in the KIND (2026-09-12). They used to be two
  // different routes, and the owner fell down the gap between them: a person route that cannot serve band
  // assets, and band media behind `/bands/{bandId}/media/…`. The second half of that is also why this branch
  // could only ever work with a band MOUNTED — it resolved the id off the seat for the active band — where an
  // identity picture has no tenant and is readable by anybody signed in.
  const bandMediaId = me.data?.seats?.find((s) => s.kind === "band" && s.bandId === band?.bandId)?.avatarMediaId;
  const bandPhoto = useAvatarUrl("band", isBand ? (band?.bandId ?? null) : null, bandMediaId);
  const personUrl = useAvatarUrl("person", isBand ? null : personId, avatarMediaId);

  return (
    <Avatar
      initials={initials}
      src={isBand ? bandPhoto : personUrl}
      size={size}
      shape={shape ?? (isBand ? "rounded" : "circle")}
      className={className}
    />
  );
}
